<template>
    <div class="main-content recap-section">
        <section class="section">
            <div class="section-header">
                <h1>Recap Tryout</h1>
            </div>

            <div class="section-body">
                <!-- Filter Section -->
                <div class="card shadow-sm mb-4">
                    <div class="card-header filter-header py-2 py-md-3">
                        <h4 class="mb-0 d-flex align-items-center text-white">
                            <i class="fa fa-filter mr-2"></i>
                            <span>Filter Data</span>
                        </h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <!-- Tryout Selection (Searchable) -->
                            <div class="col-12 col-md-6 col-lg-3 mb-3">
                                <label class="font-weight-medium text-muted mb-1">Pilih Tryout <span class="text-danger">*</span></label>
                                <div class="searchable-select-wrapper">
                                    <div class="searchable-select-box form-control" v-on:click="toggleTryoutDropdown">
                                        <span v-if="!filter.tryoutID" class="text-muted">-- Pilih Tryout --</span>
                                        <span v-else>{{ getSelectedTryoutName }}</span>
                                        <i class="fa fa-chevron-down float-right mt-1"></i>
                                    </div>
                                    <!-- Overlay for mobile -->
                                    <div class="dropdown-overlay" v-if="showTryoutDropdown" v-on:click="closeTryoutDropdown"></div>
                                    <div class="searchable-select-dropdown" v-if="showTryoutDropdown">
                                        <!-- Mobile header with close button -->
                                        <div class="dropdown-mobile-header d-md-none">
                                            <span class="font-weight-bold">Pilih Tryout</span>
                                            <button type="button" class="close-btn" v-on:click="closeTryoutDropdown">
                                                <i class="fa fa-times"></i>
                                            </button>
                                        </div>
                                        <div class="search-input-wrapper">
                                            <i class="fa fa-search search-icon"></i>
                                            <input 
                                                type="text" 
                                                v-model="tryoutSearchQuery" 
                                                class="form-control search-input" 
                                                placeholder="Cari tryout..."
                                                v-on:click.stop
                                                ref="tryoutSearchInput"
                                            />
                                        </div>
                                        <div class="searchable-select-options">
                                            <div 
                                                class="searchable-select-item" 
                                                v-for="tryout in filteredTryouts" 
                                                v-bind:key="tryout.id" 
                                                v-on:click="selectTryout(tryout)"
                                                v-bind:class="{ 'selected': filter.tryoutID === tryout.id }"
                                            >
                                                {{ tryout.name }}
                                            </div>
                                            <div v-if="filteredTryouts.length === 0" class="searchable-select-empty">
                                                <i class="fa fa-inbox"></i> Tidak ada tryout ditemukan
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Branch Selection (Multi-select) -->
                            <div class="col-12 col-md-6 col-lg-3 mb-3">
                                <label class="font-weight-medium text-muted mb-1">Lokasi BTA</label>
                                <div class="multiselect-wrapper">
                                    <div class="multiselect-box form-control" v-on:click="toggleBranchDropdown">
                                        <span v-if="filter.branchIDs.length === 0">Semua Lokasi</span>
                                        <span v-else>{{ filter.branchIDs.length }} lokasi dipilih</span>
                                        <i class="fa fa-chevron-down float-right mt-1"></i>
                                    </div>
                                    <div class="multiselect-dropdown" v-if="showBranchDropdown">
                                        <div class="multiselect-item" v-on:click="selectAllBranches">
                                            <input type="checkbox" v-bind:checked="filter.branchIDs.length === 0" /> Semua Lokasi
                                        </div>
                                        <div class="multiselect-item" v-for="branch in filterOptions.branches" v-bind:key="branch.id" v-on:click="toggleBranch(branch.id)">
                                            <input type="checkbox" v-bind:checked="filter.branchIDs.indexOf(branch.id) > -1" /> {{ branch.name }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Grade Selection (Multi-select) -->
                            <div class="col-12 col-md-6 col-lg-3 mb-3">
                                <label class="font-weight-medium text-muted mb-1">Prodi BTA</label>
                                <div class="multiselect-wrapper">
                                    <div class="multiselect-box form-control" v-on:click="toggleGradeDropdown">
                                        <span v-if="filter.gradeIDs.length === 0">Semua Prodi</span>
                                        <span v-else>{{ filter.gradeIDs.length }} prodi dipilih</span>
                                        <i class="fa fa-chevron-down float-right mt-1"></i>
                                    </div>
                                    <div class="multiselect-dropdown" v-if="showGradeDropdown">
                                        <div class="multiselect-item" v-on:click="selectAllGrades">
                                            <input type="checkbox" v-bind:checked="filter.gradeIDs.length === 0" /> Semua Prodi
                                        </div>
                                        <div class="multiselect-item" v-for="grade in grades" v-bind:key="grade.id" v-on:click="toggleGrade(grade.id)">
                                            <input type="checkbox" v-bind:checked="filter.gradeIDs.indexOf(grade.id) > -1" /> {{ grade.name }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Semester Selection -->
                            <div class="col-12 col-md-6 col-lg-3 mb-3">
                                <label class="font-weight-medium text-muted mb-1">Periode <span class="text-danger">*</span></label>
                                <select v-model="filter.semester" class="form-control" v-on:change="onFilterChange">
                                    <option v-for="sem in semesters" v-bind:value="sem.id" v-bind:key="sem.id">
                                        {{ sem.name }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="row mt-2">
                            <div class="col-12 text-right">
                                <button 
                                    class="btn btn-danger" 
                                    v-on:click="fetchData"
                                    v-bind:disabled="!canFetchData || loading"
                                >
                                    <i class="fa fa-search mr-1"></i>
                                    Tampilkan Data
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Branch Summary Section -->
                <div class="card shadow-sm mb-4" v-if="hasSummaryData">
                    <div class="card-header summary-header py-2 py-md-3 d-flex flex-wrap justify-content-between align-items-center">
                        <h4 class="mb-0 text-white">
                            <i class="fa fa-bar-chart mr-2"></i>
                            Rekap Pengerjaan {{ summaryData.tryout ? summaryData.tryout.name : '' }}
                        </h4>
                        <div class="d-flex align-items-center mt-2 mt-md-0">
                            <button class="btn btn-sm btn-outline-light mr-2" v-on:click="exportSummaryToExcel" v-bind:disabled="exporting">
                                <i class="fa fa-file-excel-o mr-1"></i> Export Summary
                            </button>
                        </div>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="loadingSummary" class="text-center py-5">
                            <i class="fa fa-spinner fa-spin fa-3x text-danger"></i>
                            <p class="mt-3 text-muted">Memuat ringkasan data...</p>
                        </div>

                        <div v-else class="table-responsive">
                            <table class="table table-bordered table-sm mb-0 summary-table" id="summary-table">
                                <thead class="thead-danger">
                                    <!-- Header Row 1: Main categories -->
                                    <tr>
                                        <th rowspan="2" class="text-center align-middle" style="min-width: 150px;">Lokasi BTA</th>
                                        <th v-bind:colspan="summaryData.columns.length" class="text-center">
                                            Pengerjaan TO SELESAI
                                        </th>
                                        <th rowspan="2" class="text-center align-middle" style="min-width: 80px;">Total Siswa</th>
                                        <th rowspan="2" class="text-center align-middle" style="min-width: 100px;">Belum Buka Tryout</th>
                                        <th v-bind:colspan="summaryData.columns.length" class="text-center">
                                            Selesai
                                        </th>
                                        <th v-bind:colspan="summaryData.columns.length" class="text-center">
                                            Belum Selesai
                                        </th>
                                    </tr>
                                    <!-- Header Row 2: Sub-columns -->
                                    <tr>
                                        <!-- Pengerjaan TO SELESAI sub-columns -->
                                        <th 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'pct_' + col.name"
                                            class="text-center th-subject-col"
                                            v-bind:title="col.code || col.fullName || col.name"
                                        >
                                            <div class="subject-label">{{ col.shortName || col.label || col.name }}</div>
                                            <small v-if="col.code" class="subtest-code" v-bind:data-tooltip="col.code">
                                                {{ col.code }}
                                            </small>
                                        </th>

                                        <!-- Selesai sub-columns -->
                                        <th 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'done_' + col.name"
                                            class="text-center th-subject-col"
                                            v-bind:title="col.code || col.fullName || col.name"
                                        >
                                            <div class="subject-label">{{ col.shortName || col.label || col.name }}</div>
                                            <small v-if="col.code" class="subtest-code" v-bind:data-tooltip="col.code">
                                                {{ col.code }}
                                            </small>
                                        </th>

                                        <!-- Belum Selesai sub-columns -->
                                        <th 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'notdone_' + col.name"
                                            class="text-center th-subject-col"
                                            v-bind:title="col.code || col.fullName || col.name"
                                        >
                                            <div class="subject-label">{{ col.shortName || col.label || col.name }}</div>
                                            <small v-if="col.code" class="subtest-code" v-bind:data-tooltip="col.code">
                                                {{ col.code }}
                                            </small>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in summaryData.summary" v-bind:key="row.branchID">
                                        <td class="font-weight-medium">{{ row.branchName }}</td>
                                        <!-- Percentage columns with Progress Bar -->
                                        <td 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'pct_data_' + col.name + '_' + row.branchID"
                                            class="p-1"
                                            style="min-width: 120px;"
                                        >
                                            <div class="progress-cell">
                                                <div class="progress" style="height: 22px; background-color: #e9ecef;">
                                                    <div 
                                                        class="progress-bar" 
                                                        role="progressbar" 
                                                        v-bind:style="getProgressBarStyle(row.columns[col.name] ? row.columns[col.name].completedPercentage : 0)"
                                                        v-bind:aria-valuenow="row.columns[col.name] ? row.columns[col.name].completedPercentage : 0"
                                                        aria-valuemin="0" 
                                                        aria-valuemax="100"
                                                    >
                                                    </div>
                                                </div>
                                                <span class="progress-text">{{ row.columns[col.name] ? row.columns[col.name].completedPercentage.toFixed(2) + '%' : '0%' }}</span>
                                            </div>
                                        </td>
                                        <!-- Total Siswa -->
                                        <td class="text-center font-weight-bold align-middle">{{ row.totalStudents }}</td>
                                        <!-- Belum Buka Tryout -->
                                        <td class="text-center align-middle" style="background-color: #fff3cd;">
                                            {{ getTotalNotStarted(row) }}
                                        </td>
                                        <!-- Selesai columns -->
                                        <td 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'done_data_' + col.name + '_' + row.branchID"
                                            class="text-center align-middle"
                                            style="background-color: #d4edda;"
                                        >
                                            {{ row.columns[col.name] ? row.columns[col.name].completed : 0 }}
                                        </td>
                                        <!-- Belum Selesai columns (partial + notStarted) -->
                                        <td 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'notdone_data_' + col.name + '_' + row.branchID"
                                            class="text-center align-middle"
                                            style="background-color: #f8d7da;"
                                        >
                                            {{ row.columns[col.name] ? (row.columns[col.name].partial + row.columns[col.name].notStarted) : 0 }}
                                        </td>
                                    </tr>
                                    <!-- Total Row -->
                                    <tr class="font-weight-bold" style="background-color: #e2e3e5;">
                                        <td class="align-middle">Total</td>
                                        <!-- Total Percentage columns with Progress Bar -->
                                        <td 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'total_pct_' + col.name"
                                            class="p-1"
                                            style="min-width: 120px;"
                                        >
                                            <div class="progress-cell">
                                                <div class="progress" style="height: 22px; background-color: #d6d8db;">
                                                    <div 
                                                        class="progress-bar" 
                                                        role="progressbar" 
                                                        v-bind:style="getProgressBarStyle(summaryData.totals.columns[col.name] ? summaryData.totals.columns[col.name].completedPercentage : 0)"
                                                        v-bind:aria-valuenow="summaryData.totals.columns[col.name] ? summaryData.totals.columns[col.name].completedPercentage : 0"
                                                        aria-valuemin="0" 
                                                        aria-valuemax="100"
                                                    >
                                                    </div>
                                                </div>
                                                <span class="progress-text font-weight-bold">{{ summaryData.totals.columns[col.name] ? summaryData.totals.columns[col.name].completedPercentage.toFixed(2) + '%' : '0%' }}</span>
                                            </div>
                                        </td>
                                        <!-- Total Siswa -->
                                        <td class="text-center align-middle">{{ summaryData.totals.totalStudents }}</td>
                                        <!-- Total Belum Buka -->
                                        <td class="text-center align-middle" style="background-color: #fff3cd;">
                                            {{ getTotalNotStartedAll() }}
                                        </td>
                                        <!-- Total Selesai columns -->
                                        <td 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'total_done_' + col.name"
                                            class="text-center align-middle"
                                            style="background-color: #c3e6cb;"
                                        >
                                            {{ summaryData.totals.columns[col.name] ? summaryData.totals.columns[col.name].completed : 0 }}
                                        </td>
                                        <!-- Total Belum Selesai columns -->
                                        <td 
                                            v-for="col in summaryData.columns" 
                                            v-bind:key="'total_notdone_' + col.name"
                                            class="text-center align-middle"
                                            style="background-color: #f5c6cb;"
                                        >
                                            {{ summaryData.totals.columns[col.name] ? (summaryData.totals.columns[col.name].partial + summaryData.totals.columns[col.name].notStarted) : 0 }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Result Section -->
                <div class="card shadow-sm" v-if="hasData">
                    <div class="card-header bg-white py-2 py-md-3 d-flex flex-wrap justify-content-between align-items-center">
                        <h4 class="mb-0">
                            <i class="fa fa-table text-danger mr-2"></i>
                            Hasil Recap: {{ recapData.tryout ? recapData.tryout.name : '' }}
                        </h4>
                        <div class="d-flex align-items-center mt-2 mt-md-0">
                            <span class="badge badge-danger mr-3">{{ recapData.totalStudents }} Siswa</span>
                            <button class="btn btn-sm btn-outline-success mr-2" v-on:click="exportToExcel" v-bind:disabled="exporting">
                                <i class="fa fa-file-excel-o mr-1"></i> Export XLS
                            </button>
                            <button class="btn btn-sm btn-outline-secondary" v-on:click="copyToClipboard">
                                <i class="fa fa-copy mr-1"></i> Copy
                            </button>
                        </div>
                    </div>

                    <div class="card-body p-0">
                        <!-- Loading -->
                        <div v-if="loading" class="text-center py-5">
                            <i class="fa fa-spinner fa-spin fa-3x text-danger"></i>
                            <p class="mt-3 text-muted">Memuat data...</p>
                        </div>

                        <!-- Table -->
                        <div v-else class="table-responsive">
                            <table class="table table-bordered table-hover table-sm mb-0 recap-table" id="recap-table">
                                <thead class="thead-danger">
                                    <tr>
                                        <th class="text-center" style="min-width: 120px;">No Peserta</th>
                                        <th style="min-width: 180px;">Nama Siswa</th>
                                        <th style="min-width: 120px;">Lokasi BTA</th>
                                        <th style="min-width: 150px;">Asal Sekolah</th>
                                        <th class="text-center" style="min-width: 60px;">Foto</th>
                                        <th style="min-width: 100px;">Prodi BTA</th>
                                        <th style="min-width: 100px;">Kelas BTA</th>
                                        <!-- Dynamic columns for subjects/groups -->
                                        <th 
                                            v-for="col in recapData.columns" 
                                            v-bind:key="col.key"
                                            class="text-center th-subject-col"
                                            v-bind:title="col.code || col.fullName || col.label"
                                        >
                                            <div class="subject-label">{{ col.label }}</div>
                                            <small v-if="col.code" class="subtest-code" v-bind:data-tooltip="col.code">
                                                {{ col.code }}
                                            </small>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="!recapData.students || recapData.students.length === 0">
                                        <td v-bind:colspan="7 + (recapData.columns ? recapData.columns.length : 0)" class="text-center py-4">
                                            <i class="fa fa-inbox fa-2x text-muted"></i>
                                            <p class="mb-0 mt-2 text-muted">Tidak ada data</p>
                                        </td>
                                    </tr>
                                    <tr v-for="(student, index) in recapData.students" v-bind:key="student.userID">
                                        <td class="text-center">{{ student.identity }}</td>
                                        <td class="font-weight-medium">{{ student.userName }}</td>
                                        <td>{{ student.branchName || '-' }}</td>
                                        <td>{{ student.schoolName || '-' }}</td>
                                        <td class="text-center">
                                            <a v-if="student.imageUrl" href="javascript:void(0)" v-on:click="showPhotoPreview(student)" class="btn btn-sm btn-outline-primary view-photo-btn">
                                                <i class="fa fa-eye mr-1"></i>View
                                            </a>
                                            <span v-else class="text-muted">-</span>
                                        </td>
                                        <td>{{ student.gradeName }}</td>
                                        <td>{{ student.branchClassName || '-' }}</td>
                                        <!-- Dynamic data columns -->
                                        <td 
                                            v-for="col in recapData.columns" 
                                            v-bind:key="col.key + '_' + student.userID"
                                            class="text-center"
                                        >
                                            <!-- Subject type columns -->
                                            <template v-if="col.type === 'subject'">
                                                <span 
                                                    v-if="student.attempts[col.key] === 'completed'" 
                                                    class="badge badge-success badge-attempt" 
                                                    title="Status: Selesai"
                                                >
                                                    <i class="fa fa-check"></i>
                                                </span>
                                                <span 
                                                    v-else-if="student.attempts[col.key] === 'progress'" 
                                                    class="badge badge-warning badge-attempt" 
                                                    title="Status: Sedang Dikerjakan"
                                                >
                                                    <i class="fa fa-clock-o"></i>
                                                </span>
                                                <span v-else class="badge badge-secondary badge-attempt" title="Belum Dikerjakan">-</span>
                                            </template>
                                            <!-- Group type columns (summary) -->
                                            <template v-else>
                                                <span 
                                                    class="badge badge-attempt"
                                                    v-bind:class="student.attempts[col.key] === 'SELESAI' ? 'badge-success' : 'badge-danger'"
                                                    v-bind:title="student.attempts[col.key] === 'SELESAI' ? 'Ringkasan: Semua subtes dalam grup selesai' : 'Ringkasan: Belum semua subtes dalam grup selesai'"
                                                >
                                                    {{ student.attempts[col.key] }}
                                                </span>
                                            </template>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                </div>
            </div>

                <!-- No data yet -->
                <div v-else-if="!loading" class="card shadow-sm">
                    <div class="card-body text-center py-5">
                        <i class="fa fa-file-text-o fa-4x text-muted mb-3"></i>
                        <h5 class="text-muted">Silakan pilih filter di atas dan klik "Tampilkan Data"</h5>
                    </div>
                </div>

                <!-- Photo Preview Modal -->
                <div class="modal-overlay" v-if="showModal" v-on:click.self="closePhotoPreview">
                    <div class="modal-container">
                        <div class="modal-content-custom">
                            <div class="modal-header">
                                <h5 class="modal-title">{{ previewStudent ? previewStudent.userName : 'Preview Foto' }}</h5>
                                <button type="button" class="close" v-on:click="closePhotoPreview">
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div class="modal-body text-center">
                                <img v-if="previewStudent && previewStudent.imageUrl" v-bind:src="previewStudent.imageUrl" class="img-fluid preview-photo" v-bind:alt="previewStudent.userName" />
                            </div>
                            <div class="modal-footer">
                                <a v-if="previewStudent && previewStudent.imageUrl" v-bind:href="previewStudent.imageUrl" target="_blank" class="btn btn-sm btn-outline-primary">
                                    <i class="fa fa-external-link mr-1"></i> Buka di Tab Baru
                                </a>
                                <button type="button" class="btn btn-secondary" v-on:click="closePhotoPreview">Tutup</button>
                            </div>
                        </div>
                    </div>
                </div>


            </div>
        </section>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        name: 'TryoutRecap',
        computed: {
            ...mapState([
                'profile',
                'term'
            ]),
            canFetchData: function() {
                return this.filter.tryoutID !== '';
            },
            hasData: function() {
                return this.recapData.students && this.recapData.students.length >= 0 && this.dataFetched;
            },
            filteredTryouts: function() {
                var self = this;
                if (!this.tryoutSearchQuery) {
                    return this.filterOptions.tryouts;
                }
                var query = this.tryoutSearchQuery.toLowerCase();
                return this.filterOptions.tryouts.filter(function(tryout) {
                    return tryout.name.toLowerCase().indexOf(query) > -1;
                });
            },
            getSelectedTryoutName: function() {
                var self = this;
                if (!this.filter.tryoutID) return '';
                var selected = this.filterOptions.tryouts.find(function(t) {
                    return t.id === self.filter.tryoutID;
                });
                return selected ? selected.name : '';
            },
            hasSummaryData: function() {
                return this.summaryData.summary && this.summaryData.summary.length > 0 && this.summaryFetched;
            }
        },
        watch: {
            term: {
                handler: function(newVal, oldVal) {
                    console.log('Term watcher triggered:', newVal);
                    if (newVal && newVal.id) {
                        this.fetchFilterOptions();
                    }
                },
                immediate: true,
                deep: true
            }
        },
        data: function() {
            return {
                loading: false,
                exporting: false,
                dataFetched: false,
                showBranchDropdown: false,
                showGradeDropdown: false,
                filter: {
                    tryoutID: '',
                    branchIDs: [],
                    gradeIDs: [],
                    semester: 'semester1'
                },
                filterOptions: {
                    tryouts: [],
                    branches: [],
                    isAdmin: false
                },
                semesters: [
                    { id: 'semester1', name: 'Semester 1' },
                    { id: 'semester2', name: 'Semester 2' },
                    { id: 'intensive', name: 'Intensif' }
                ],
                grades: [],
                recapData: {
                    students: [],
                    columns: [],
                    tryout: null,
                    totalStudents: 0
                },
                previewStudent: null,
                showModal: false,
                showTryoutDropdown: false,
                tryoutSearchQuery: '',
                loadingSummary: false,
                summaryFetched: false,
                summaryData: {
                    summary: [],
                    columns: [],
                    totals: {
                        totalStudents: 0,
                        columns: {}
                    },
                    tryout: null
                }
            };
        },
        methods: {
            toggleTryoutDropdown: function() {
                this.showTryoutDropdown = !this.showTryoutDropdown;
                this.showBranchDropdown = false;
                this.showGradeDropdown = false;
                if (this.showTryoutDropdown) {
                    this.tryoutSearchQuery = '';
                    var self = this;
                    this.$nextTick(function() {
                        if (self.$refs.tryoutSearchInput) {
                            self.$refs.tryoutSearchInput.focus();
                        }
                    });
                }
            },
            selectTryout: function(tryout) {
                this.filter.tryoutID = tryout.id;
                this.showTryoutDropdown = false;
                this.tryoutSearchQuery = '';
                this.onFilterChange();
            },
            closeTryoutDropdown: function() {
                this.showTryoutDropdown = false;
                this.tryoutSearchQuery = '';
            },
            toggleBranchDropdown: function() {
                this.showBranchDropdown = !this.showBranchDropdown;
                this.showGradeDropdown = false;
                this.showTryoutDropdown = false;
            },
            toggleGradeDropdown: function() {
                this.showGradeDropdown = !this.showGradeDropdown;
                this.showBranchDropdown = false;
                this.showTryoutDropdown = false;
            },
            selectAllBranches: function() {
                this.filter.branchIDs = [];
                this.onBranchChange();
            },
            selectAllGrades: function() {
                this.filter.gradeIDs = [];
                this.onFilterChange();
            },
            toggleBranch: function(branchId) {
                var idx = this.filter.branchIDs.indexOf(branchId);
                if (idx > -1) {
                    this.filter.branchIDs.splice(idx, 1);
                } else {
                    this.filter.branchIDs.push(branchId);
                }
                this.onBranchChange();
            },
            toggleGrade: function(gradeId) {
                var idx = this.filter.gradeIDs.indexOf(gradeId);
                if (idx > -1) {
                    this.filter.gradeIDs.splice(idx, 1);
                } else {
                    this.filter.gradeIDs.push(gradeId);
                }
                this.onFilterChange();
            },
            closeDropdowns: function() {
                this.showBranchDropdown = false;
                this.showGradeDropdown = false;
            },
            showPhotoPreview: function(student) {
                this.previewStudent = student;
                this.showModal = true;
            },
            closePhotoPreview: function() {
                this.showModal = false;
                this.previewStudent = null;
            },

            fetchFilterOptions: function() {
                if (!this.term || !this.term.id) {
                    console.log('Term not available yet');
                    return;
                }
                
                var self = this;
                console.log('Fetching filter options with termID:', this.term.id);
                
                this.axios.get(process.env.API_URL + '/recap/filter-options', {
                    params: {
                        termID: this.term.id
                    }
                }).then(function(response) {
                    console.log('Filter options response:', response.data);
                    if (response.data.status) {
                        self.filterOptions = response.data.data;
                        
                        // If not admin and only one branch, auto-select it
                        if (!self.filterOptions.isAdmin && self.filterOptions.branches.length === 1) {
                            self.filter.branchIDs = [self.filterOptions.branches[0].id];
                        }
                        // Always fetch grades
                        self.fetchGrades();
                    }
                }).catch(function(error) {
                    console.error('Error fetching filter options:', error);
                });
            },
            fetchGrades: function() {
                if (!this.term || !this.term.id) return;
                
                var self = this;
                var params = {
                    termID: this.term.id
                };
                
                if (this.filter.branchIDs.length > 0) {
                    params.branchIDs = this.filter.branchIDs.join(',');
                }

                this.axios.get(process.env.API_URL + '/recap/grades', { params: params }).then(function(response) {
                    if (response.data.status) {
                        self.grades = response.data.data;
                    }
                });
            },
            onBranchChange: function() {
                this.filter.gradeIDs = [];
                this.fetchGrades();
                this.onFilterChange();
            },
            onFilterChange: function() {
                // Reset data when filter changes
                this.dataFetched = false;
                this.summaryFetched = false;
            },
            fetchData: function() {
                if (!this.canFetchData) return;

                this.loading = true;
                this.dataFetched = false;
                this.summaryFetched = false;
                this.closeDropdowns();

                // Fetch both main data and summary data
                this.fetchMainData();
                this.fetchSummaryData();
            },
            fetchMainData: function() {
                var self = this;
                var params = {
                    termID: this.term.id,
                    tryoutID: this.filter.tryoutID,
                    semester: this.filter.semester
                };

                if (this.filter.branchIDs.length > 0) {
                    params.branchIDs = this.filter.branchIDs.join(',');
                }
                if (this.filter.gradeIDs.length > 0) {
                    params.gradeIDs = this.filter.gradeIDs.join(',');
                }

                this.axios.get(process.env.API_URL + '/recap/data', { params: params }).then(function(response) {
                    if (response.data.status) {
                        self.recapData = response.data.data;
                        self.dataFetched = true;
                    }
                }).catch(function(error) {
                    console.error('Error fetching recap data:', error);
                    var errorMessage = 'Gagal memuat data';
                    if (error.response && error.response.data && error.response.data.message) {
                        errorMessage = error.response.data.message;
                    }
                    self.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: errorMessage
                    });
                }).finally(function() {
                    self.loading = false;
                });
            },
            fetchSummaryData: function() {
                var self = this;
                this.loadingSummary = true;
                
                var params = {
                    termID: this.term.id,
                    tryoutID: this.filter.tryoutID,
                    semester: this.filter.semester
                };

                if (this.filter.branchIDs.length > 0) {
                    params.branchIDs = this.filter.branchIDs.join(',');
                }
                if (this.filter.gradeIDs.length > 0) {
                    params.gradeIDs = this.filter.gradeIDs.join(',');
                }

                this.axios.get(process.env.API_URL + '/recap/summary-by-branch', { params: params }).then(function(response) {
                    if (response.data.status) {
                        self.summaryData = response.data.data;
                        self.summaryFetched = true;
                    }
                }).catch(function(error) {
                    console.error('Error fetching summary data:', error);
                }).finally(function() {
                    self.loadingSummary = false;
                });
            },
            getPercentageStyle: function(percentage) {
                // Color gradient from red (0%) to green (100%)
                var red, green;
                if (percentage < 50) {
                    red = 255;
                    green = Math.round((percentage / 50) * 255);
                } else {
                    red = Math.round(255 - ((percentage - 50) / 50) * 255);
                    green = 255;
                }
                return {
                    backgroundColor: 'rgba(' + red + ', ' + green + ', 0, 0.3)',
                    fontWeight: 'bold'
                };
            },
            getProgressBarStyle: function(percentage) {
                // Color based on percentage thresholds
                var color;
                if (percentage < 25) {
                    color = '#dc3545'; // Red
                } else if (percentage < 50) {
                    color = '#fd7e14'; // Orange
                } else if (percentage < 75) {
                    color = '#ffc107'; // Yellow
                } else {
                    color = '#28a745'; // Green
                }
                return {
                    width: percentage + '%',
                    backgroundColor: color
                };
            },
            getTotalNotStarted: function(row) {
                // Get the count of students who haven't started any tryout subject
                // We use the first column's notStarted as indicator (they haven't started anything)
                if (!row.columns || !this.summaryData.columns || this.summaryData.columns.length === 0) {
                    return 0;
                }
                var firstCol = this.summaryData.columns[0];
                return row.columns[firstCol.name] ? row.columns[firstCol.name].notStarted : 0;
            },
            getTotalNotStartedAll: function() {
                // Get total not started from totals
                if (!this.summaryData.totals || !this.summaryData.totals.columns || !this.summaryData.columns || this.summaryData.columns.length === 0) {
                    return 0;
                }
                var firstCol = this.summaryData.columns[0];
                return this.summaryData.totals.columns[firstCol.name] ? this.summaryData.totals.columns[firstCol.name].notStarted : 0;
            },
            exportSummaryToExcel: function() {
                var self = this;
                this.exporting = true;

                // Build headers
                var headers = ['Lokasi BTA'];
                
                // Add percentage columns
                this.summaryData.columns.forEach(function(col) {
                    var colTitle = col.code ? (col.name + ' - ' + col.code) : col.name;
                    headers.push(colTitle + ' (%)');
                });
                
                headers.push('Total Siswa');
                headers.push('Belum Buka Tryout');
                
                // Add Selesai columns
                this.summaryData.columns.forEach(function(col) {
                    var colTitle = col.code ? (col.name + ' - ' + col.code) : col.name;
                    headers.push('Selesai ' + colTitle);
                });
                
                // Add Belum Selesai columns
                this.summaryData.columns.forEach(function(col) {
                    var colTitle = col.code ? (col.name + ' - ' + col.code) : col.name;
                    headers.push('Belum Selesai ' + colTitle);
                });

                var rows = [];
                this.summaryData.summary.forEach(function(row) {
                    var rowData = [row.branchName];
                    
                    // Percentage columns
                    self.summaryData.columns.forEach(function(col) {
                        var pct = row.columns[col.name] ? row.columns[col.name].completedPercentage.toFixed(2) + '%' : '0%';
                        rowData.push(pct);
                    });
                    
                    rowData.push(row.totalStudents);
                    rowData.push(self.getTotalNotStarted(row));
                    
                    // Selesai columns
                    self.summaryData.columns.forEach(function(col) {
                        rowData.push(row.columns[col.name] ? row.columns[col.name].completed : 0);
                    });
                    
                    // Belum Selesai columns
                    self.summaryData.columns.forEach(function(col) {
                        var notDone = row.columns[col.name] ? (row.columns[col.name].partial + row.columns[col.name].notStarted) : 0;
                        rowData.push(notDone);
                    });
                    
                    rows.push(rowData);
                });

                // Add totals row
                var totalRow = ['Total'];
                self.summaryData.columns.forEach(function(col) {
                    var pct = self.summaryData.totals.columns[col.name] ? self.summaryData.totals.columns[col.name].completedPercentage.toFixed(2) + '%' : '0%';
                    totalRow.push(pct);
                });
                totalRow.push(self.summaryData.totals.totalStudents);
                totalRow.push(self.getTotalNotStartedAll());
                self.summaryData.columns.forEach(function(col) {
                    totalRow.push(self.summaryData.totals.columns[col.name] ? self.summaryData.totals.columns[col.name].completed : 0);
                });
                self.summaryData.columns.forEach(function(col) {
                    var notDone = self.summaryData.totals.columns[col.name] ? (self.summaryData.totals.columns[col.name].partial + self.summaryData.totals.columns[col.name].notStarted) : 0;
                    totalRow.push(notDone);
                });
                rows.push(totalRow);

                // Helper function to escape XML special characters
                function escapeXml(str) {
                    if (str === null || str === undefined) return '';
                    return String(str)
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&apos;');
                }

                // Build XML Spreadsheet
                var xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
                xml += '<?mso-application progid="Excel.Sheet"?>\n';
                xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
                xml += '  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
                
                xml += '  <Styles>\n';
                xml += '    <Style ss:ID="Header">\n';
                xml += '      <Font ss:Bold="1" ss:Color="#FFFFFF"/>\n';
                xml += '      <Interior ss:Color="#28A745" ss:Pattern="Solid"/>\n';
                xml += '      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>\n';
                xml += '    </Style>\n';
                xml += '    <Style ss:ID="Data">\n';
                xml += '      <Alignment ss:Vertical="Center"/>\n';
                xml += '    </Style>\n';
                xml += '  </Styles>\n';
                
                xml += '  <Worksheet ss:Name="Summary">\n';
                xml += '    <Table>\n';
                
                // Add header row
                xml += '      <Row>\n';
                headers.forEach(function(header) {
                    xml += '        <Cell ss:StyleID="Header"><Data ss:Type="String">' + escapeXml(header) + '</Data></Cell>\n';
                });
                xml += '      </Row>\n';
                
                // Add data rows
                rows.forEach(function(row) {
                    xml += '      <Row>\n';
                    row.forEach(function(cell) {
                        xml += '        <Cell ss:StyleID="Data"><Data ss:Type="String">' + escapeXml(cell) + '</Data></Cell>\n';
                    });
                    xml += '      </Row>\n';
                });
                
                xml += '    </Table>\n';
                xml += '  </Worksheet>\n';
                xml += '</Workbook>';

                var blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
                var link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = 'rekap_pengerjaan_' + (this.summaryData.tryout ? this.summaryData.tryout.name : 'tryout') + '_' + new Date().toISOString().slice(0, 10) + '.xls';
                link.click();
                URL.revokeObjectURL(link.href);

                this.exporting = false;
                this.$swal({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Data ringkasan berhasil diexport ke Excel',
                    timer: 2000,
                    showConfirmButton: false
                });
            },
            exportToExcel: function() {
                var self = this;
                this.exporting = true;

                // Build table data for export
                var headers = ['No Peserta', 'Nama Siswa', 'Lokasi BTA', 'Asal Sekolah', 'Foto URL', 'Prodi BTA', 'Kelas BTA'];
                
                // Add dynamic column headers
                this.recapData.columns.forEach(function(col) {
                    var headerTitle = col.code ? (col.label + ' - ' + col.code) : col.label;
                    headers.push(headerTitle);
                });

                var rows = [];
                this.recapData.students.forEach(function(student) {
                    var row = [
                        student.identity || '',
                        student.userName || '',
                        student.branchName || '',
                        student.schoolName || '',
                        student.imageUrl || '',
                        student.gradeName || '',
                        student.branchClassName || ''
                    ];

                    // Add dynamic column values
                    self.recapData.columns.forEach(function(col) {
                        var val = student.attempts[col.key];
                        if (col.type === 'subject') {
                            if (val === 'completed') val = 'SELESAI';
                            else if (val === 'progress') val = 'PROSES';
                            else val = 'BELUM';
                        }
                        row.push(val || '');
                    });

                    rows.push(row);
                });

                // Helper function to escape XML special characters
                function escapeXml(str) {
                    if (str === null || str === undefined) return '';
                    return String(str)
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&apos;');
                }

                // Build XML Spreadsheet (SpreadsheetML format)
                var xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
                xml += '<?mso-application progid="Excel.Sheet"?>\n';
                xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
                xml += '  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
                
                // Add styles for header
                xml += '  <Styles>\n';
                xml += '    <Style ss:ID="Header">\n';
                xml += '      <Font ss:Bold="1" ss:Color="#FFFFFF"/>\n';
                xml += '      <Interior ss:Color="#C9302C" ss:Pattern="Solid"/>\n';
                xml += '      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>\n';
                xml += '    </Style>\n';
                xml += '    <Style ss:ID="Data">\n';
                xml += '      <Alignment ss:Vertical="Center"/>\n';
                xml += '    </Style>\n';
                xml += '  </Styles>\n';
                
                xml += '  <Worksheet ss:Name="Recap">\n';
                xml += '    <Table>\n';
                
                // Add header row
                xml += '      <Row>\n';
                headers.forEach(function(header) {
                    xml += '        <Cell ss:StyleID="Header"><Data ss:Type="String">' + escapeXml(header) + '</Data></Cell>\n';
                });
                xml += '      </Row>\n';
                
                // Add data rows
                rows.forEach(function(row) {
                    xml += '      <Row>\n';
                    row.forEach(function(cell) {
                        xml += '        <Cell ss:StyleID="Data"><Data ss:Type="String">' + escapeXml(cell) + '</Data></Cell>\n';
                    });
                    xml += '      </Row>\n';
                });
                
                xml += '    </Table>\n';
                xml += '  </Worksheet>\n';
                xml += '</Workbook>';

                // Create blob and download as .xls (Excel will read XML format correctly)
                var blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
                var link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = 'recap_' + (this.recapData.tryout ? this.recapData.tryout.name : 'tryout') + '_' + new Date().toISOString().slice(0, 10) + '.xls';
                link.click();
                URL.revokeObjectURL(link.href);

                this.exporting = false;
                this.$swal({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Data berhasil diexport ke Excel',
                    timer: 2000,
                    showConfirmButton: false
                });
            },
            copyToClipboard: function() {
                var self = this;
                
                // Build table data for clipboard
                var headers = ['No Peserta', 'Nama Siswa', 'Lokasi BTA', 'Asal Sekolah', 'Foto URL', 'Prodi BTA', 'Kelas BTA'];
                
                // Add dynamic column headers
                this.recapData.columns.forEach(function(col) {
                    headers.push(col.label);
                });

                var rows = [];
                this.recapData.students.forEach(function(student) {
                    var row = [
                        student.identity || '',
                        student.userName || '',
                        student.branchName || '',
                        student.schoolName || '',
                        student.imageUrl || '',
                        student.gradeName || '',
                        student.branchClassName || ''
                    ];

                    // Add dynamic column values
                    self.recapData.columns.forEach(function(col) {
                        var val = student.attempts[col.key];
                        if (col.type === 'subject') {
                            if (val === 'completed') val = 'SELESAI';
                            else if (val === 'progress') val = 'PROSES';
                            else val = 'BELUM';
                        }
                        row.push(val || '');
                    });

                    rows.push(row);
                });

                // Create text content
                var textContent = headers.join('\t') + '\n';
                rows.forEach(function(row) {
                    textContent += row.join('\t') + '\n';
                });

                // Copy to clipboard
                if (navigator.clipboard) {
                    navigator.clipboard.writeText(textContent).then(function() {
                        self.$swal({
                            icon: 'success',
                            title: 'Berhasil',
                            text: 'Data berhasil dicopy ke clipboard',
                            timer: 2000,
                            showConfirmButton: false
                        });
                    }).catch(function(err) {
                        console.error('Failed to copy:', err);
                        self.fallbackCopy(textContent);
                    });
                } else {
                    this.fallbackCopy(textContent);
                }
            },
            fallbackCopy: function(text) {
                var textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                try {
                    document.execCommand('copy');
                    this.$swal({
                        icon: 'success',
                        title: 'Berhasil',
                        text: 'Data berhasil dicopy ke clipboard',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } catch (err) {
                    console.error('Fallback copy failed:', err);
                    this.$swal({
                        icon: 'error',
                        title: 'Gagal',
                        text: 'Gagal menyalin data ke clipboard'
                    });
                }
                
                document.body.removeChild(textArea);
            },
            closeDropdowns: function() {
                this.showBranchDropdown = false;
                this.showGradeDropdown = false;
                this.showTryoutDropdown = false;
            }
        },
        created: function() {
            document.title = 'Recap Tryout - ' + process.env.APP_NAME;
        },
        mounted: function() {
            var self = this;
            // Retry after a short delay if term wasn't available initially
            setTimeout(function() {
                if (self.term && self.term.id && self.filterOptions.tryouts.length === 0) {
                    console.log('Retrying fetchFilterOptions after delay');
                    self.fetchFilterOptions();
                }
            }, 500);

            // Close dropdowns when clicking outside
            document.addEventListener('click', function(e) {
                if (!e.target.closest('.multiselect-wrapper') && !e.target.closest('.searchable-select-wrapper')) {
                    self.closeDropdowns();
                }
            });
        }
    };
</script>

<style scoped>
    .recap-section {
        padding-bottom: 20px;
    }

    .filter-header {
        background: linear-gradient(135deg, #C9302C 0%, #A02622 100%);
    }

    .recap-table {
        font-size: 0.85rem;
    }

    .summary-table {
        font-size: 0.8rem;
        border-collapse: collapse;
    }

    .summary-table th,
    .summary-table td {
        vertical-align: middle;
        border: 1px solid #A02622;
    }

    .summary-table thead th {
        white-space: nowrap;
        text-align: center;
        background-color: #C9302C;
        color: #fff;
        border-color: #A02622;
        padding: 8px 10px;
    }

    .summary-table thead tr:first-child th {
        border-bottom: 1px solid #A02622;
    }

    .summary-table thead tr:last-child th {
        border-top: none;
    }

    .summary-table tbody td {
        border-color: #dee2e6;
    }

    .summary-header {
        background: linear-gradient(135deg, #C9302C 0%, #A02622 100%);
    }

    .progress-cell {
        position: relative;
        min-width: 100px;
    }

    .progress-cell .progress {
        border-radius: 4px;
        overflow: hidden;
    }

    .progress-cell .progress-bar {
        transition: width 0.3s ease;
    }

    .progress-cell .progress-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 0.75rem;
        font-weight: bold;
        color: #333;
        text-shadow: 0 0 2px #fff, 0 0 2px #fff, 0 0 2px #fff;
        white-space: nowrap;
    }

    .thead-danger th {
        background-color: #C9302C;
        color: #fff;
        border-color: #A02622;
    }

    .recap-table th {
        white-space: nowrap;
        vertical-align: middle;
        position: relative;
    }

    .recap-table td {
        vertical-align: middle;
    }

    .student-photo {
        width: 40px;
        height: 40px;
        object-fit: cover;
        border-radius: 4px;
    }

    .badge-success {
        background-color: #28A745;
        color: #fff;
    }

    .badge-warning {
        background-color: #FFC107;
        color: #212529;
    }

    .badge-secondary {
        background-color: #6c757d;
        color: #fff;
    }

    .badge-danger {
        background-color: #C9302C;
        color: #fff;
    }

    .btn-danger {
        background-color: #C9302C;
        border-color: #C9302C;
    }

    .btn-danger:hover {
        background-color: #A02622;
        border-color: #A02622;
    }

    .text-danger {
        color: #C9302C !important;
    }

    /* Searchable Select styles */
    .searchable-select-wrapper {
        position: relative;
    }

    .searchable-select-box {
        cursor: pointer;
        background-color: #fff;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .searchable-select-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background-color: #fff;
        border: 1px solid #ced4da;
        border-radius: 0.25rem;
        z-index: 1001;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        margin-top: 2px;
    }

    .dropdown-overlay {
        display: none;
    }

    .dropdown-mobile-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #eee;
        background-color: #f8f9fa;
    }

    .dropdown-mobile-header .close-btn {
        background: none;
        border: none;
        font-size: 1.2rem;
        color: #666;
        padding: 4px 8px;
        cursor: pointer;
    }

    .dropdown-mobile-header .close-btn:hover {
        color: #333;
    }

    .search-input-wrapper {
        position: relative;
        padding: 8px;
        border-bottom: 1px solid #eee;
    }

    .search-icon {
        position: absolute;
        left: 18px;
        top: 50%;
        transform: translateY(-50%);
        color: #999;
        font-size: 0.85rem;
    }

    .search-input {
        padding-left: 30px;
        font-size: 0.9rem;
    }

    .searchable-select-options {
        max-height: 200px;
        overflow-y: auto;
    }

    .searchable-select-item {
        padding: 10px 12px;
        cursor: pointer;
        font-size: 0.9rem;
        transition: background-color 0.15s;
    }

    .searchable-select-item:hover {
        background-color: #f8f9fa;
    }

    .searchable-select-item.selected {
        background-color: #e8f4ff;
        color: #007bff;
        font-weight: 500;
    }

    .searchable-select-empty {
        padding: 20px;
        text-align: center;
        color: #999;
        font-size: 0.85rem;
    }

    /* Multiselect styles */
    .multiselect-wrapper {
        position: relative;
    }

    .multiselect-box {
        cursor: pointer;
        background-color: #fff;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .multiselect-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background-color: #fff;
        border: 1px solid #ced4da;
        border-radius: 0.25rem;
        max-height: 200px;
        overflow-y: auto;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    }

    .multiselect-item {
        padding: 8px 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .multiselect-item:hover {
        background-color: #f8f9fa;
    }

    .multiselect-item input[type="checkbox"] {
        margin-right: 8px;
    }

    /* Mobile responsive - Tablet */
    @media (max-width: 992px) {
        .section-header h1 {
            font-size: 1.5rem;
        }

        .card-header h4 {
            font-size: 1rem;
        }
    }

    /* Mobile responsive - Phone */
    @media (max-width: 768px) {
        .recap-section {
            padding: 10px;
        }

        .section-header h1 {
            font-size: 1.25rem;
        }

        .card-header.filter-header h4 {
            font-size: 0.9rem;
        }

        .card-header.filter-header h4 span {
            display: none;
        }

        .card-header.filter-header h4::after {
            content: 'Filter';
        }

        /* Result card header - stack vertically */
        .card-header.bg-white {
            flex-direction: column;
            align-items: flex-start !important;
        }

        .card-header.bg-white h4 {
            font-size: 0.95rem;
            margin-bottom: 10px;
        }

        .card-header.bg-white .d-flex {
            width: 100%;
            justify-content: space-between;
        }

        /* Table responsive */
        .recap-table {
            font-size: 0.7rem;
        }

        .recap-table th,
        .recap-table td {
            padding: 4px 6px;
            white-space: nowrap;
        }

        .recap-table th {
            font-size: 0.65rem;
        }

        /* Smaller view button on mobile */
        .view-photo-btn {
            padding: 1px 5px;
            font-size: 0.65rem;
        }

        .view-photo-btn i {
            display: none;
        }

        /* Action buttons */
        .btn-sm {
            padding: 4px 8px;
            font-size: 0.75rem;
        }

        .btn-sm i {
            margin-right: 2px !important;
        }

        /* Badge */
        .badge-danger {
            font-size: 0.7rem;
            padding: 4px 8px;
        }

        /* Filter form on mobile */
        .filter-header {
            padding: 8px 12px !important;
        }

        .card-body {
            padding: 12px;
        }

        .form-control {
            font-size: 0.85rem;
            padding: 6px 10px;
        }

        label {
            font-size: 0.8rem;
        }

        /* Multiselect on mobile */
        .multiselect-box {
            font-size: 0.85rem;
            padding: 6px 10px;
        }

        .multiselect-dropdown {
            max-height: 180px;
        }

        .multiselect-item {
            padding: 6px 10px;
            font-size: 0.8rem;
        }

        /* Searchable Select on mobile */
        .searchable-select-box {
            font-size: 0.85rem;
            padding: 6px 10px;
            min-height: 38px;
        }

        .searchable-select-dropdown {
            position: fixed;
            top: auto;
            bottom: 0;
            left: 0;
            right: 0;
            max-height: 70vh;
            border-radius: 12px 12px 0 0;
            z-index: 9999;
            box-shadow: 0 -4px 20px rgba(0,0,0,0.25);
        }

        .dropdown-overlay {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 9998;
        }

        .dropdown-mobile-header {
            display: flex !important;
        }

        .search-input-wrapper {
            padding: 12px;
            border-bottom: 1px solid #eee;
            background-color: #f8f9fa;
        }

        .search-input {
            padding: 10px 10px 10px 35px;
            font-size: 16px; /* Prevents iOS zoom on focus */
            border-radius: 8px;
        }

        .searchable-select-options {
            max-height: calc(70vh - 120px);
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
        }

        .searchable-select-item {
            padding: 14px 16px;
            font-size: 0.95rem;
            border-bottom: 1px solid #f0f0f0;
        }

        .searchable-select-item:active {
            background-color: #e9ecef;
        }

        /* Tampilkan Data button */
        .btn-danger {
            width: 100%;
            padding: 10px;
        }

        /* Summary table mobile */
        .summary-table {
            font-size: 0.65rem;
        }

        .progress-cell {
            min-width: 80px;
        }

        .progress-cell .progress {
            height: 18px !important;
        }

        .progress-cell .progress-text {
            font-size: 0.6rem;
        }
    }

    /* Extra small devices */
    @media (max-width: 480px) {
        .recap-table {
            font-size: 0.6rem;
        }

        .recap-table th,
        .recap-table td {
            padding: 3px 4px;
        }

        .view-photo-btn {
            padding: 1px 4px;
            font-size: 0.6rem;
        }

        .card-header.bg-white h4 {
            font-size: 0.85rem;
        }

        .card-header.bg-white h4 i {
            display: none;
        }

        /* Stack export buttons */
        .card-header.bg-white .d-flex.align-items-center {
            flex-wrap: wrap;
            gap: 5px;
        }

        .card-header.bg-white .badge {
            order: 3;
            width: 100%;
            text-align: center;
            margin-top: 5px;
            margin-right: 0 !important;
        }

        /* Modal adjustments */
        .modal-container {
            width: 95%;
        }

        .modal-content-custom .modal-header,
        .modal-content-custom .modal-footer {
            padding: 10px 15px;
        }

        .modal-content-custom .modal-body {
            padding: 15px;
        }

        .preview-photo {
            max-height: 300px;
        }
    }

    /* View Photo Button styles */
    .view-photo-btn {
        padding: 2px 8px;
        font-size: 0.75rem;
        border-radius: 4px;
        transition: all 0.2s ease;
    }

    .view-photo-btn:hover {
        background-color: #007bff;
        color: #fff;
        transform: scale(1.05);
    }

    .preview-photo {
        max-width: 100%;
        max-height: 400px;
        border-radius: 8px;
    }

    /* Custom Modal Overlay */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    .modal-container {
        max-width: 500px;
        width: 90%;
        animation: slideIn 0.3s ease;
    }

    @keyframes slideIn {
        from { 
            opacity: 0;
            transform: translateY(-20px);
        }
        to { 
            opacity: 1;
            transform: translateY(0);
        }
    }

    .modal-content-custom {
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        overflow: hidden;
    }

    .modal-content-custom .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 20px;
        border-bottom: 1px solid #dee2e6;
        background-color: #f8f9fa;
    }

    .modal-content-custom .modal-title {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
    }

    .modal-content-custom .close {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: #666;
        line-height: 1;
    }

    .modal-content-custom .close:hover {
        color: #000;
    }

    .modal-content-custom .modal-body {
        padding: 20px;
    }

    .modal-content-custom .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 15px 20px;
        border-top: 1px solid #dee2e6;
        background-color: #f8f9fa;
    }

    /* Batasi lebar kolom subtes agar tidak melar saat ada bab panjang */
.th-subject-col {
    max-width: 100px;
    min-width: 75px;
    padding: 6px 4px !important;
}

.subject-label {
    font-weight: 700;
    font-size: 0.85rem;
    line-height: 1.2;
}

/* Format teks kode bab: jika kepanjangan akan dipotong rapi dengan '...' */
/* Teks kode kecil di header */
.subtest-code {
    position: relative;
    display: block;
    max-width: 95px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 2px auto 0 auto;
    font-size: 9px;
    line-height: 1.1;
    opacity: 0.85;
    cursor: default; /* Kursor normal biasa (bukan tanda tanya) */
}

/* Balon Tooltip Kustom */
.subtest-code[data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #212529;
    color: #ffffff;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.3;
    padding: 6px 10px;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    white-space: normal;
    min-width: 140px;
    max-width: 260px;
    width: max-content;
    text-align: center;
    z-index: 1050;
    pointer-events: none;

    /* Pengaturan transisi dan delay */
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
    transition-delay: 0s; /* Hilang langsung saat kursor menjauh */
}

/* Muncul tepat setelah 0.5 detik diarahkan */
.subtest-code[data-tooltip]:hover::after {
    opacity: 1;
    visibility: visible;
    transition-delay: 0.5s; /* Jeda 0.5 detik baru muncul */
}

.subtest-code:hover {
    opacity: 1;
}

.badge-attempt {
    padding: 6px 9px;
    font-size: 0.85rem;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.badge-clickable {
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    user-select: none;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

.badge-clickable:hover {
    transform: translateY(-2px) scale(1.18);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
    filter: brightness(1.1);
}

.badge-clickable:active {
    transform: translateY(0) scale(1);
}

.reset-modal-container {
    max-width: 480px;
}

.hint-banner {
    background-color: #f8f9fa;
}

</style>



// WEBPACK FOOTER //
// src/components/tryout/recap/Index.vue